// LLM generated - Model Claude 4 Sonnet
import React, { useRef } from 'react';
import { Menu, useMantineTheme } from '@mantine/core';
import {
  IconDotsVertical,
  IconPencil,
  IconMoodPlus,
  IconPin,
  IconPinned,
  IconPinnedOff,
  IconTrash,
  IconVolume3,
  IconFlag,
} from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';

import { Avatar } from '../../../Avatar';
import { Button } from '../../../Button';
import { Text } from '../../../Text';
import type { PostProps } from './types';
import { useStyles } from './Post.styles';
import CommissionerTag from './CommissionerTag';
import ReactionDropdown, {
  type ReactionDropdownRef,
} from '../ChatBubble/ReactionDropdown/ReactionDropdown';

function Post({
  id,
  avatar,
  handle,
  title,
  content,
  timestamp,
  isPinned = false,
  isCommissioner = false,
  reactionCount = 0,
  reactions = [],
  isExpandable = false,
  isExpanded = false,
  onToggleExpand,
  onEditClick,
  onPinClick,
  onDeleteClick,
  onMuteClick,
  onReportClick,
  isCurrentUser = false,
  isMuted = false,
  onReactionSelect,
  onReactionRemove,
  availableReactions = [],
  isLoadingReactions = false,
  currentUserHandle,
  userReactions = [],
}: PostProps) {
  const { classes, theme } = useStyles({ isCommissioner });
  const { t } = useTranslation('chat');
  const reactionDropdownRef = useRef<ReactionDropdownRef>(null);

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpand?.(id);
  };

  const handleReactionSelect = (reactionCode: string) => {
    onReactionSelect?.(id, reactionCode);
  };

  const handleReactionRemove = (reactionCode: string) => {
    onReactionRemove?.(id, reactionCode);
  };

  return (
    <div className={classes.postContainer}>
      {isPinned && (
        <div className={classes.pinnedLabel}>
          <IconPin size={16} color={theme.colors.gray2[4]} />
          <Text variant="subhead-small" color={theme.colors.gray2[4]}>
            {t('post.pinned')}
          </Text>
        </div>
      )}

      <div className={classes.header}>
        <div className={classes.avatarContainer}>
          <Avatar src={avatar} alt={t('ariaLabels.userAvatar', { handle })} size={40} />
          {/* Always show commissioner badge for posts since only commissioners can create posts */}
          <div className={classes.commissionerBadge}>
            <CommissionerTag size={18} />
          </div>
        </div>

        <div className={classes.headerContent}>
          <div className={classes.userInfo}>
            <Text variant="subhead-medium" color={theme.colors.gray2[6]} className={classes.handle}>
              {handle}
            </Text>
            <Text variant="title-small" color={theme.colors.gray2[4]} className={classes.timestamp}>
              {timestamp}
            </Text>
          </div>
        </div>

        {/* Show dropdown menu for commissioners (edit/pin/delete) or entrants (mute/report) */}
        {((isCommissioner && isCurrentUser) || !isCommissioner) && (
          <Menu radius="md" position="bottom-end" width={200}>
            <Menu.Target>
              <div
                role="button"
                tabIndex={0}
                className={classes.menuButton}
                aria-label={t('ariaLabels.postOptions')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    // Menu.Target will handle the click
                  }
                }}
              >
                <IconDotsVertical size={16} color={theme.colors.gray2[4]} />
              </div>
            </Menu.Target>

            <Menu.Dropdown>
              {/* Commissioner options */}
              {isCommissioner && isCurrentUser && (
                <>
                  <Menu.Item icon={<IconPencil size={16} />} onClick={onEditClick}>
                    <Text variant="subhead-medium" color={theme.colors.gray2[6]}>
                      {t('post.edit')}
                    </Text>
                  </Menu.Item>

                  <Menu.Item
                    icon={isPinned ? <IconPinnedOff size={16} /> : <IconPinned size={16} />}
                    onClick={onPinClick}
                  >
                    <Text variant="subhead-medium" color={theme.colors.gray2[6]}>
                      {isPinned ? t('post.unpin') : t('post.pin')}
                    </Text>
                  </Menu.Item>

                  <Menu.Item
                    icon={<IconTrash size={16} color={theme.colors.red[4]} />}
                    onClick={onDeleteClick}
                  >
                    <Text variant="subhead-medium" color={theme.colors.red[4]}>
                      {t('post.delete')}
                    </Text>
                  </Menu.Item>
                </>
              )}

              {/* Entrant options */}
              {!isCommissioner && (
                <>
                  <Menu.Item icon={<IconVolume3 size={16} />} onClick={onMuteClick}>
                    <Text variant="subhead-medium" color={theme.colors.gray2[6]}>
                      {isMuted ? t('post.unmute', { handle }) : t('post.mute', { handle })}
                    </Text>
                  </Menu.Item>

                  <Menu.Item
                    icon={<IconFlag size={16} color={theme.colors.red[4]} />}
                    onClick={() => onReportClick?.(handle, id)}
                  >
                    <Text variant="subhead-medium" color={theme.colors.red[4]}>
                      {t('post.report')}
                    </Text>
                  </Menu.Item>
                </>
              )}
            </Menu.Dropdown>
          </Menu>
        )}
      </div>

      <Text variant="subhead-large" color={theme.colors.gray2[6]} className={classes.title}>
        {title}
      </Text>

      <div
        className={`${classes.content} ${
          isExpandable && !isExpanded ? classes.contentCollapsed : ''
        }`}
      >
        <Text variant="body-large" color={theme.colors.gray2[6]}>
          {content}
        </Text>
      </div>

      {isExpandable && (
        <div className={classes.showMoreContainer}>
          <Button variant="primary-text" size="small" onClick={handleToggleExpand}>
            <Text variant="label-medium" color={theme.colors.blue[3]}>
              {isExpanded ? t('post.showLess') : t('post.showMore')}
            </Text>
          </Button>
        </div>
      )}

      <div className={classes.footer}>
        <div className={classes.reactionContainer}>
          <ReactionDropdown
            ref={reactionDropdownRef}
            availableReactions={availableReactions}
            onReactionSelect={handleReactionSelect}
            onReactionRemove={handleReactionRemove}
            userReactions={userReactions}
            isLoading={isLoadingReactions}
            disabled={isLoadingReactions || availableReactions.length === 0}
            reactions={reactions}
            reactionCount={reactionCount}
            variant="other" // Posts always use 'other' variant styling
          />
        </div>
      </div>
    </div>
  );
}

export { Post };
